import { useRef, useState, useCallback } from 'react';

// reCAPTCHA v2 ("I'm not a robot") checkbox fallback, shown when the invisible
// v3 verification fails. Renders once into a persistent container and is reset
// on each challenge so a fresh, single-use token is produced every attempt.
export default function useRecaptchaV2() {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const tokenRef = useRef(null);
  const onSolvedRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const render = useCallback(() => {
    if (!window.grecaptcha || !containerRef.current) return;
    const siteKey = process.env.REACT_APP_GOOGLE_V2_SITE_KEY;

    if (widgetIdRef.current === null) {
      window.grecaptcha.ready(() => {
        try {
          widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token) => {
              tokenRef.current = token;
              if (onSolvedRef.current) onSolvedRef.current(token);
            },
            'expired-callback': () => { tokenRef.current = null; },
            'error-callback': () => { tokenRef.current = null; },
          });
        } catch (e) { /* already rendered */ }
      });
    } else {
      try { window.grecaptcha.reset(widgetIdRef.current); } catch (e) { }
      tokenRef.current = null;
    }
  }, []);

  // Reveal the challenge. If it is already visible (e.g. a repeated failure),
  // reset the existing widget so the user gets a fresh, unused token.
  const show = useCallback((onSolved) => {
    onSolvedRef.current = onSolved;
    setVisible((wasVisible) => {
      if (wasVisible) render();
      return true;
    });
  }, [render]);

  const hide = useCallback(() => {
    setVisible(false);
    tokenRef.current = null;
    if (window.grecaptcha && widgetIdRef.current !== null) {
      try { window.grecaptcha.reset(widgetIdRef.current); } catch (e) { }
    }
  }, []);

  return { containerRef, visible, show, hide, render, tokenRef };
}
